class StatisticsComponent extends HTMLElement {
  #observer = null;
  #hasAnimated = false;
  #abort = null;

  connectedCallback() {
    this.#initCarousel();

    if (this.dataset.animate !== 'true' || this.#hasAnimated || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Start progress bars empty; they fill together with the count-up.
    this.#resetBars();

    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.3 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abort?.abort();
  }

  #resetBars() {
    this.querySelectorAll('.statistics__bar > i[data-pct]').forEach((bar) => {
      bar.style.setProperty('--statistics-pct', '0%');
    });
  }

  #onIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    this.#hasAnimated = true;
    this.#observer?.disconnect();
    this.#animateMetrics();
  };

  #animateMetrics() {
    const duration = 1400;
    const start = performance.now();
    const lang = document.documentElement.lang || undefined;

    const items = [...this.querySelectorAll('.statistics__item')].map((item) => {
      const number = item.querySelector('[data-count-up-value]');
      const bar = item.querySelector('.statistics__bar > i[data-pct]');
      const numberText = number?.dataset.countUpValue?.trim() ?? '';
      const numberTarget = /^\d+(?:\.\d+)?$/.test(numberText) ? Number(numberText) : null;
      const decimals = (numberText.split('.')[1] || '').length;
      const formatter =
        numberTarget === null
          ? null
          : new Intl.NumberFormat(lang, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

      return {
        number,
        bar,
        numberTarget,
        formatter,
        finalText: number?.textContent ?? '',
        barTarget: bar ? Number(bar.dataset.pct) : null,
      };
    });

    const update = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;

      for (const item of items) {
        if (item.number && item.numberTarget !== null) {
          item.number.textContent = item.formatter.format(item.numberTarget * eased);
        }
        if (item.bar && item.barTarget !== null) {
          item.bar.style.setProperty('--statistics-pct', `${item.barTarget * eased}%`);
        }
      }

      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        for (const item of items) {
          if (item.number) item.number.textContent = item.finalText;
          if (item.bar && item.barTarget !== null) {
            item.bar.style.setProperty('--statistics-pct', `${item.barTarget}%`);
          }
        }
      }
    };

    requestAnimationFrame(update);
  }

  #initCarousel() {
    const grid = this.querySelector('.statistics__grid');
    const dotsWrap = this.querySelector('[data-statistics-dots]');
    if (!grid || !dotsWrap) return;

    const dots = [...dotsWrap.querySelectorAll('[data-statistics-dot]')];
    const cards = [...grid.querySelectorAll('.statistics__item')];
    if (!dots.length || cards.length !== dots.length) return;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const setActive = (index) =>
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

    const nearestIndex = () => {
      const gridRect = grid.getBoundingClientRect();
      const center = gridRect.left + gridRect.width / 2;
      let best = 0;
      let bestDistance = Infinity;
      cards.forEach((card, i) => {
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    };

    let frame = 0;
    const sync = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const scrollable = grid.scrollWidth - grid.clientWidth > 1;
        grid.tabIndex = scrollable ? 0 : -1;
        setActive(nearestIndex());
      });
    };

    const scrollToIndex = (index) => {
      const clamped = Math.max(0, Math.min(index, cards.length - 1));
      const card = cards[clamped];
      if (!card) return;
      setActive(clamped);
      const gridRect = grid.getBoundingClientRect();
      const rect = card.getBoundingClientRect();
      const left = grid.scrollLeft + (rect.left - gridRect.left) - (grid.clientWidth - rect.width) / 2;
      grid.scrollTo({ left: Math.max(0, left), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    };

    dots.forEach((dot, i) => dot.addEventListener('click', () => scrollToIndex(i), { signal }));
    grid.addEventListener('scroll', sync, { signal, passive: true });
    window.addEventListener('resize', sync, { signal });
    grid.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollToIndex(nearestIndex() - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollToIndex(nearestIndex() + 1);
        }
      },
      { signal }
    );

    sync();
  }
}

if (!customElements.get('statistics-component')) {
  customElements.define('statistics-component', StatisticsComponent);
}
